import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API } from '../App';
import { Trophy, Target, Flag, Coins, MapPin, Users, Calendar, ExternalLink, Award } from 'lucide-react';

/**
 * PublicRecap — no-auth-required page that renders a round recap. Powered by
 * the `share_id` produced from the Share Recap flow inside the Scorecard flow.
 * Anyone with the link can view the full recap.
 */
export default function PublicRecap() {
  const { shareId } = useParams();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/recaps/${shareId}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'Recap not found' : 'Could not load recap');
        const data = await res.json();
        if (!cancelled) setRecap(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green" />
      </div>
    );
  }

  if (error || !recap) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-bundle-cream p-6 text-center">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-3">
          <Flag className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="font-heading font-black text-2xl text-bundle-green mb-1">Recap unavailable</h1>
        <p className="font-body text-sm text-gray-600 max-w-xs mb-4">{error || 'This recap could not be loaded.'}</p>
        <Link to="/" className="text-sm font-bold text-bundle-green underline">Go to Bundle Golf Club →</Link>
      </div>
    );
  }

  const {
    course_name, date, low_gross, low_net, most_birdies, most_skins,
    ctp = [], leaderboard = [], created_by_name,
    pars = {}, skins_by_hole = [], bundle_wins = {},
  } = recap;

  const podium = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);
  const displayDate = date ? new Date(date).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const hasHoleData = leaderboard.some(p => p.scores && Object.keys(p.scores).length > 0);
  const hasBundleWins = bundle_wins && (bundle_wins.front9 || bundle_wins.back9 || bundle_wins.overall);

  return (
    <div className="min-h-screen bg-bundle-cream pb-16" data-testid="public-recap">
      {/* Hero */}
      <div className="bg-gradient-to-br from-bundle-green via-bundle-green to-bundle-green-dark text-white px-5 pt-6 pb-8 shadow-lg">
        <div className="flex items-center justify-center gap-1.5 mb-3">
          <span className="w-1 h-1 rounded-full bg-bundle-gold" />
          <span className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-gold">Round Recap</span>
          <span className="w-1 h-1 rounded-full bg-bundle-gold" />
        </div>
        <h1 className="font-heading font-black text-3xl leading-tight text-center">{course_name}</h1>
        {displayDate && (
          <p className="flex items-center justify-center gap-1.5 mt-1.5 text-white/80 font-body text-sm">
            <Calendar className="w-3.5 h-3.5" />
            {displayDate}
          </p>
        )}
        {created_by_name && (
          <p className="text-center mt-2 text-white/60 font-body text-[11px]">Shared by {created_by_name}</p>
        )}
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-4">
        {/* Highlights row */}
        <div className="grid grid-cols-1 gap-2 mb-4">
          {low_gross && (
            <HighlightCard icon={Trophy} tint="from-amber-400 to-yellow-500" label="Low Gross" name={low_gross.name} value={low_gross.total} sub={formatToPar(low_gross.score_to_par)} />
          )}
          {low_net && (
            <HighlightCard icon={Target} tint="from-emerald-500 to-emerald-700" label="Low Net" name={low_net.name} value={low_net.net_total} sub={`HCP ${low_net.handicap}`} />
          )}
          {most_birdies && (most_birdies.birdies > 0 || most_birdies.eagles > 0) && (
            <HighlightCard icon={Flag} tint="from-sky-500 to-cyan-600" label="Most Birdies" name={most_birdies.name} value={most_birdies.birdies} sub={most_birdies.eagles > 0 ? `+ ${most_birdies.eagles} 🦅` : ''} />
          )}
          {most_skins && most_skins.skins > 0 && (
            <HighlightCard icon={Coins} tint="from-yellow-500 to-amber-600" label="Most Skins" name={most_skins.name} value={most_skins.skins} sub={most_skins.skins === 1 ? 'skin' : 'skins'} />
          )}
        </div>

        {/* Bundle Wins */}
        {hasBundleWins && (
          <div className="bg-white rounded-2xl shadow-sm border border-bundle-cream-dark p-4 mb-4" data-testid="recap-bundle-wins">
            <h3 className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green flex items-center gap-1.5 mb-3">
              <Award className="w-3.5 h-3.5 text-bundle-gold" /> Bundle Wins
            </h3>
            <div className="grid grid-cols-3 gap-2">
              <BundleWinCell label="Front 9" win={bundle_wins.front9} />
              <BundleWinCell label="Back 9" win={bundle_wins.back9} />
              <BundleWinCell label="Overall" win={bundle_wins.overall} />
            </div>
          </div>
        )}

        {/* CTP */}
        {ctp.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-bundle-cream-dark p-4 mb-4">
            <h3 className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green flex items-center gap-1.5 mb-2">
              <MapPin className="w-3.5 h-3.5 text-bundle-gold" /> Closest to Pin
            </h3>
            <div className="space-y-1.5">
              {ctp.map((c) => (
                <div key={c.hole} className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Hole {c.hole}</span>
                  <span className="font-bold text-bundle-green">{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skins per hole */}
        {skins_by_hole.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-bundle-cream-dark p-4 mb-4" data-testid="recap-skins-by-hole">
            <h3 className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green flex items-center gap-1.5 mb-2">
              <Coins className="w-3.5 h-3.5 text-bundle-gold" /> Skins Won ({skins_by_hole.length})
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {skins_by_hole.map((s) => (
                <span
                  key={s.hole}
                  className="inline-flex items-center gap-1 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 font-semibold px-2 py-0.5 rounded-full"
                >
                  <span className="text-amber-500">H{s.hole}</span>
                  <span className="text-gray-800">{s.winner_name}</span>
                  {s.score != null && <span className="text-gray-500">({s.score})</span>}
                </span>
              ))}
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              {18 - skins_by_hole.length} hole{18 - skins_by_hole.length !== 1 ? 's' : ''} tied — no winner
            </p>
          </div>
        )}

        {/* Podium */}
        {podium.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-bundle-cream-dark p-4 mb-3">
            <h3 className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green flex items-center gap-1.5 mb-3">
              <Trophy className="w-3.5 h-3.5 text-bundle-gold" /> Podium
            </h3>
            {podium.map((p, i) => (
              <PodiumRow key={p.name + i} rank={i + 1} player={p} />
            ))}
          </div>
        )}

        {/* Rest of leaderboard */}
        {rest.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-bundle-cream-dark p-4 mb-4">
            <h3 className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green flex items-center gap-1.5 mb-2">
              <Users className="w-3.5 h-3.5 text-bundle-gold" /> Full Leaderboard
            </h3>
            <div className="space-y-1">
              {rest.map((p, i) => (
                <div key={p.name + i} className="flex items-center gap-2 text-sm border-b border-gray-50 py-1.5 last:border-b-0">
                  <span className="w-5 text-gray-400 font-body text-xs">{i + 4}.</span>
                  <span className="flex-1 font-body text-gray-800 truncate">{p.name}</span>
                  {p.handicap != null && (
                    <span className="hidden xs:inline w-10 text-right text-[10px] text-gray-400 font-semibold">HCP {p.handicap}</span>
                  )}
                  <span className="font-bold text-bundle-green">{p.total}</span>
                  <span className="w-10 text-right text-xs text-gray-500">{formatToPar(p.score_to_par)}</span>
                  {p.net_total != null && (
                    <span className="w-14 text-right text-xs text-emerald-700 font-semibold">Net {p.net_total}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Hole-by-Hole Scorecard */}
        {hasHoleData ? (
          <div className="bg-white rounded-2xl shadow-sm border border-bundle-cream-dark p-4 mb-4" data-testid="recap-scorecard-table">
            <h3 className="font-heading font-black text-[10px] uppercase tracking-widest text-bundle-green flex items-center gap-1.5 mb-3">
              <Flag className="w-3.5 h-3.5 text-bundle-gold" /> Scorecard
            </h3>
            <HoleTable label="Front 9" range={front9Range} pars={pars} players={leaderboard} />
            <HoleTable label="Back 9" range={back9Range} pars={pars} players={leaderboard} className="mt-4" />
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
              Colors: <span className="text-red-500 font-semibold">eagle/birdie</span>
              {' · '}<span className="text-bundle-green font-semibold">par</span>
              {' · '}<span className="text-gray-500 font-semibold">bogey</span>
              {' · '}<span className="text-gray-700 font-semibold">double+</span>
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-bundle-cream-dark p-4 mb-4 text-center" data-testid="recap-scorecard-unavailable">
            <Flag className="w-6 h-6 text-gray-300 mx-auto mb-1" />
            <p className="font-heading font-black text-[10px] uppercase tracking-widest text-gray-400 mb-1">Scorecard</p>
            <p className="text-xs text-gray-500">Scorecard unavailable for this round.</p>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-bundle-green hover:text-bundle-green-dark"
          >
            Posted on <span className="text-bundle-gold">The Bundle Golf Club</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

const front9Range = Array.from({ length: 9 }, (_, i) => i + 1);
const back9Range = Array.from({ length: 9 }, (_, i) => i + 10);

function HighlightCard({ icon: Icon, tint, label, name, value, sub }) {
  return (
    <div className={`rounded-2xl bg-gradient-to-br ${tint} text-white p-4 shadow-md`}>
      <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white/90 mb-1">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="flex items-baseline justify-between">
        <span className="font-heading font-black text-lg truncate">{name}</span>
        <div className="flex items-baseline gap-1 flex-shrink-0">
          <span className="font-heading font-black text-2xl">{value}</span>
          {sub && <span className="text-xs font-semibold opacity-80">{sub}</span>}
        </div>
      </div>
    </div>
  );
}

function BundleWinCell({ label, win }) {
  if (!win || !win.name) {
    return (
      <div className="rounded-xl bg-bundle-cream/60 border border-bundle-cream-dark p-2.5 text-center">
        <div className="text-[9px] uppercase tracking-widest font-black text-gray-400 mb-1">{label}</div>
        <div className="text-xs text-gray-400">—</div>
      </div>
    );
  }
  return (
    <div className="rounded-xl bg-gradient-to-br from-bundle-gold/15 to-bundle-cream border border-bundle-gold/40 p-2.5 text-center">
      <div className="text-[9px] uppercase tracking-widest font-black text-bundle-gold mb-1">{label}</div>
      <div className="text-xs font-bold text-bundle-green truncate" title={win.name}>{win.name}</div>
      {win.total != null && (
        <div className="font-heading font-black text-lg text-bundle-green leading-none mt-0.5">{win.total}</div>
      )}
    </div>
  );
}

function PodiumRow({ rank, player }) {
  const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
  return (
    <div className={`flex items-center gap-2 py-2 border-b border-gray-50 last:border-b-0`}>
      <span className="text-lg w-6">{medals[rank]}</span>
      <span className="flex-1 font-bold text-gray-800 truncate">{player.name}</span>
      {player.handicap != null && (
        <span className="hidden xs:inline text-[10px] text-gray-400 font-semibold">HCP {player.handicap}</span>
      )}
      <span className="font-heading font-black text-lg text-bundle-green">{player.total}</span>
      <span className="w-10 text-right text-xs text-gray-500">{formatToPar(player.score_to_par)}</span>
      {player.net_total != null && (
        <span className="w-14 text-right text-xs text-emerald-700 font-semibold">Net {player.net_total}</span>
      )}
    </div>
  );
}

function HoleTable({ label, range, pars, players, className = '' }) {
  const totalLabel = label === 'Front 9' ? 'OUT' : 'IN';
  const parTotal = range.reduce((sum, h) => sum + (Number(pars?.[h]) || 0), 0);
  return (
    <div className={`overflow-x-auto -mx-1 ${className}`}>
      <div className="text-[10px] uppercase tracking-wider font-black text-bundle-green/70 mb-1 ml-1">{label}</div>
      <table className="w-full text-[11px] border-separate border-spacing-0">
        <thead>
          <tr className="bg-bundle-green/10">
            <th className="px-1.5 py-1.5 text-left font-bold text-bundle-green text-[10px] sticky left-0 bg-bundle-green/10 z-10">Hole</th>
            {range.map(h => (
              <th key={h} className="px-1.5 py-1.5 text-center text-bundle-green text-[10px] font-bold w-7">{h}</th>
            ))}
            <th className="px-1.5 py-1.5 text-center text-bundle-green text-[10px] font-black bg-bundle-green/20">{totalLabel}</th>
          </tr>
          <tr className="bg-bundle-cream/60 text-[10px] text-gray-500">
            <td className="px-1.5 py-1 font-semibold sticky left-0 bg-bundle-cream/60 z-10">Par</td>
            {range.map(h => (
              <td key={h} className="px-1.5 py-1 text-center">{pars?.[h] ?? '—'}</td>
            ))}
            <td className="px-1.5 py-1 text-center font-bold">{parTotal || '—'}</td>
          </tr>
        </thead>
        <tbody>
          {players.map((p, i) => {
            const scores = p.scores || {};
            let subTotal = 0;
            let counted = 0;
            range.forEach(h => {
              const s = Number(scores?.[h]);
              if (Number.isFinite(s)) { subTotal += s; counted++; }
            });
            return (
              <tr key={(p.name || '') + i} className="border-b border-bundle-cream-dark/40">
                <td className="px-1.5 py-1 sticky left-0 bg-white z-10 font-semibold text-gray-800 truncate max-w-[100px]" title={p.name}>{p.name}</td>
                {range.map(h => {
                  const s = scores?.[h];
                  const par = pars?.[h];
                  return (
                    <td key={h} className="px-1 py-1 text-center">
                      <ScoreCell score={s} par={par} />
                    </td>
                  );
                })}
                <td className="px-1.5 py-1 text-center font-black text-bundle-green bg-bundle-green/5">
                  {counted > 0 ? subTotal : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScoreCell({ score, par }) {
  if (score == null || score === '') return <span className="text-gray-300">—</span>;
  const s = Number(score);
  const p = Number(par);
  if (!Number.isFinite(s)) return <span className="text-gray-300">—</span>;
  if (!Number.isFinite(p)) return <span className="text-gray-700 font-semibold">{s}</span>;
  const diff = s - p;
  // Style: eagle (double ring), birdie (red circle), par (green), bogey (gray), double+ (dark)
  if (diff <= -2) return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full ring-2 ring-red-500 bg-red-50 text-red-600 font-black text-[10px]">{s}</span>;
  if (diff === -1) return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white font-black text-[10px]">{s}</span>;
  if (diff === 0) return <span className="font-bold text-bundle-green">{s}</span>;
  if (diff === 1) return <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-100 text-gray-600 font-semibold text-[10px]">{s}</span>;
  return <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-gray-700 text-white font-black text-[10px]">{s}</span>;
}

function formatToPar(v) {
  if (v == null) return '';
  if (v > 0) return `+${v}`;
  if (v === 0) return 'E';
  return `${v}`;
}
